/**
 * Signing in against the portal.
 *
 * The authorization code flow with PKCE. There is no client secret, and there
 * could not be one: this code is served to a browser, so anything it held
 * would be published. PKCE is what replaces the secret, by proving that
 * whoever redeems the code is the same party that started the exchange.
 *
 * Because the browser already holds a portal session, the token is obtained
 * without a prompt. Renewal happens quietly in the background before expiry,
 * so a long editing session never ends with a redirect that loses unsaved
 * work.
 *
 * Tokens live in `sessionStorage`: gone when the tab closes, never shared
 * with another tab's session, and never written where a later visitor to the
 * same machine could find them.
 */

import {
  UserManager,
  WebStorageStateStore,
  type User,
  type UserManagerSettings,
} from 'oidc-client-ts'

import { config } from '@/app/config'

function buildSettings(): UserManagerSettings {
  return {
    authority: config.auth.portalUrl,
    client_id: config.auth.clientId,
    redirect_uri: config.auth.redirectUri,
    silent_redirect_uri: config.auth.silentRedirectUri,
    post_logout_redirect_uri: config.auth.postLogoutRedirectUri,
    response_type: 'code',
    scope: config.auth.scopes,

    // The API enriches identity from the portal itself when a token carries
    // too little, so fetching the profile here would be a second call for
    // something already handled.
    loadUserInfo: false,

    automaticSilentRenew: true,

    // Shorter than the ten second default, because of what the wait costs
    // rather than what it saves. A provider that is going to answer answers
    // in well under a second; the full timeout is only ever spent on one that
    // will not answer at all, and it is spent with somebody watching a
    // holding screen. Embedded, it is spent after the wait for the host has
    // already elapsed, so the two stack.
    silentRequestTimeoutInSeconds: 5,

    // Session monitoring polls the provider in a hidden iframe. It buys early
    // notice that somebody signed out elsewhere, at the cost of a request
    // every few seconds for the life of the tab. Renewal failure already
    // catches that case, a little later and far more cheaply.
    monitorSession: false,

    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  }
}

export const userManager = new UserManager(buildSettings())

/** The scopes this token was actually granted. */
function grantedScopes(user: User): ReadonlySet<string> {
  return new Set((user.scope ?? '').split(/\s+/).filter(Boolean))
}

/**
 * Whether a cached token predates a change to what this application asks for.
 *
 * Worth checking, because the failure it prevents is baffling otherwise: a
 * token that verifies perfectly and is refused by the API, because it was
 * issued before the API scope was added and nothing since has forced a fresh
 * one. `offline_access` is excluded, as the provider may legitimately decline
 * to grant it.
 */
function isStale(user: User): boolean {
  const granted = grantedScopes(user)
  return config.auth.scopes
    .split(/\s+/)
    .filter((scope) => scope && scope !== 'offline_access')
    .some((scope) => !granted.has(scope))
}

/** What signing in produced, or why it did not. */
export type SignInResult =
  | { readonly kind: 'signed-in'; readonly user: User }
  | { readonly kind: 'no-session' }

/**
 * Establish a session without interrupting anyone.
 *
 * A cached token is used when it is still good. Otherwise the portal is asked
 * silently, which succeeds whenever the browser holds a portal session. When
 * it does not, this reports so rather than redirecting: the caller decides
 * whether a redirect is appropriate, since an application embedded in a frame
 * must not navigate its host away.
 */
export async function signInSilently(): Promise<SignInResult> {
  const cached = await userManager.getUser().catch(() => null)

  if (cached && !cached.expired) {
    if (!isStale(cached)) return { kind: 'signed-in', user: cached }
    // Remove it, or `signinSilent` may hand the same stale token straight back.
    console.warn('[auth] the stored token predates a scope change; asking for a new one')
    await userManager.removeUser().catch(() => undefined)
  }

  try {
    const user = await userManager.signinSilent()
    if (!user) return { kind: 'no-session' }
    if (isStale(user)) {
      // The provider answered, but without a scope that was asked for, which
      // almost always means the client registration does not list it.
      console.warn(
        '[auth] the portal granted fewer scopes than were requested; check the client registration',
        { granted: [...grantedScopes(user)], requested: config.auth.scopes },
      )
    }
    return { kind: 'signed-in', user }
  } catch {
    return { kind: 'no-session' }
  }
}

/** Send the browser to the portal to sign in, returning here afterwards. */
export async function signInWithRedirect(returnTo?: string): Promise<void> {
  await userManager.signinRedirect({ state: returnTo ?? window.location.pathname })
}

/**
 * Finish a sign-in the portal redirected back from.
 *
 * Returns where the user was going, so they land where they started rather
 * than at the front page of an application they were already inside.
 */
export async function completeSignIn(): Promise<{ user: User; returnTo: string } | null> {
  try {
    const user = await userManager.signinRedirectCallback()
    const returnTo = typeof user.state === 'string' && user.state ? user.state : '/'
    return { user, returnTo }
  } catch (error) {
    console.error('[auth] the sign-in could not be completed:', error)
    return null
  }
}

/** Finish a silent renewal. Runs inside the hidden renewal frame. */
export async function completeSilentRenew(): Promise<void> {
  try {
    await userManager.signinSilentCallback()
  } catch (error) {
    console.error('[auth] the silent renewal could not be completed:', error)
  }
}

/** Sign out here and at the portal. */
export async function signOut(): Promise<void> {
  try {
    await userManager.signoutRedirect()
  } catch (error) {
    // Signing out locally still matters when the portal cannot be reached:
    // leaving a usable token in the tab would be the worse outcome.
    console.error('[auth] the portal sign-out failed; clearing the local session:', error)
    await userManager.removeUser().catch(() => undefined)
  }
}
